/**
 * The `{% ht_dynamic_slot %}` marker that a message body holds where a Dynamic
 * content placement renders.
 */

export const DYNAMIC_SLOT_TAG = "ht_dynamic_slot";

export function dynamicSlotMarker(usageId: string): string {
  return `{% ${DYNAMIC_SLOT_TAG} id:"${usageId}" %}`;
}

/**
 * Starts every attribute the BeeFree Design tab puts on a Dynamic content
 * variant module. That module is display only, so no stored body holds one.
 */
export const DYNAMIC_CONTENT_DESIGN_TAB_ATTRIBUTE_PREFIX =
  "data-ht-dc-variant-";

/**
 * One liquid-tag `key:value` hash entry — quoted (either style) or bare.
 * Shared with `content-blocks/liquid-tag.ts` so tag-scanning tolerance stays
 * uniform.
 */
export const LIQUID_HASH_ENTRY = String.raw`\w+\s*:\s*(?:"[^"]*"|'[^']*'|[^\s%-]+)`;

const UUID_PATTERN = String.raw`[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}`;

/**
 * Matches markers emitted by {@link dynamicSlotMarker} (capture group 1 = the
 * usage id). Tolerates whitespace-trim variants, quote styles, and extra
 * `key:value` hash entries around `id`, so a future field doesn't make it
 * silently miss tags. Case tolerance lives in the pattern, not an `i` flag,
 * so consumers rebuilding via `new RegExp(source, "g")` keep it. `g`-flagged —
 * use with `matchAll` or reset `lastIndex` between uses.
 */
export const DYNAMIC_SLOT_MARKER_REGEX = new RegExp(
  String.raw`\{%-?\s*${DYNAMIC_SLOT_TAG}(?:\s+${LIQUID_HASH_ENTRY})*\s+id\s*:\s*["'](${UUID_PATTERN})["'](?:\s+${LIQUID_HASH_ENTRY})*\s*-?%\}`,
  "g",
);

const DYNAMIC_SLOT_MARKER_PRESENCE = new RegExp(
  DYNAMIC_SLOT_MARKER_REGEX.source,
);

export function hasDynamicSlotMarkers(text: string): boolean {
  return DYNAMIC_SLOT_MARKER_PRESENCE.test(text);
}

/** Copies remove markers for placements that could not be matched to the design. */
export function rewriteOrRemoveDynamicSlotMarkers(
  html: string,
  usageIdRewrites: ReadonlyMap<string, string>,
): string {
  return html.replace(
    new RegExp(DYNAMIC_SLOT_MARKER_REGEX.source, "g"),
    (_marker, usageId: string) => {
      const rewritten = usageIdRewrites.get(usageId);
      return rewritten === undefined ? "" : dynamicSlotMarker(rewritten);
    },
  );
}

export function rewriteOrRemoveDynamicSlotMarkersInKeys<Value extends object>(
  value: Value,
  keys: readonly string[],
  usageIdRewrites: ReadonlyMap<string, string>,
): Value {
  return {
    ...value,
    ...Object.fromEntries(
      keys
        .filter((key) => Object.hasOwn(value, key))
        .map((key) => {
          const text: unknown = Reflect.get(value, key);
          return [
            key,
            typeof text === "string"
              ? rewriteOrRemoveDynamicSlotMarkers(text, usageIdRewrites)
              : text,
          ];
        }),
    ),
  };
}
